import { useLocation, Outlet } from "react-router-dom";
import Sidebar from "@surgia/ui/containers/sidebar";
import { useState, useMemo } from "react";
import dashboardIcon from "@surgia/ui/icons/sidebar/dashboard.svg";
import { useTranslation } from "react-i18next";
import { SidebarItemType } from "@surgia/ui/components/sidebar/sidebarNavItem/constants";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useAuth } from "react-oidc-context";

export const Layout = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isRTL = i18n.language === "ar";
  const authState = useSelector((state: RootState) => state.auth);
  const auth = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const isAuthenticated =
    authState.globalState.access_token && authState.globalState.subject_id;

  // Create user profile from Redux global state
  const userProfile = isAuthenticated
    ? {
        name:
          authState.globalState.full_name ||
          `${authState.globalState.first_name || ""} ${authState.globalState.last_name || ""}`.trim(),
        email: authState.globalState.email,
        first_name: authState.globalState.first_name,
        last_name: authState.globalState.last_name,
        full_name: authState.globalState.full_name,
        subject_id: authState.globalState.subject_id,
      }
    : null;

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleSignOut = () => {
    try {
      // Broadcast logout to other tabs
      const authChannel = new BroadcastChannel("auth-sync");
      authChannel.postMessage({ type: "LOGOUT" });
      authChannel.close();

      // Clear local storage and session storage
      localStorage.clear();
      sessionStorage.clear();

      // Remove user and redirect
      auth.removeUser();
      auth.signoutRedirect();
    } catch (error) {
      console.error("Error during sign out:", error);
    }
  };

  const navigation = useMemo(
    () => [
      {
        type: "link" as SidebarItemType,
        href: "/",
        icon: dashboardIcon,
        label: t("shared.sidebar.dashboard"),
        isActive: location.pathname === "/",
      },
    ],
    [location.pathname, t]
  );

  return (
    <div className="min-h-screen bg-gray-100 p-1" dir={isRTL ? "rtl" : "ltr"}>
      <div className="flex h-[calc(100vh-0.5rem)] bg-white rounded-lg overflow-hidden">
        {/* Sidebar Container - only show if authenticated */}
        {isAuthenticated && (
          <div className="p-1 bg-gray-100 transition-all duration-300 ease-in-out">
            <Sidebar
              className="rounded-lg h-[99.9%]"
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={toggleSidebar}
              items={navigation}
              userProfile={userProfile}
              onSignOut={handleSignOut}
            />
          </div>
        )}

        {/* Main content - always render */}
        <div className="flex-1 overflow-auto bg-white transition-all duration-300 ease-in-out">
          <main className="px-5 pt-5 h-full">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;




